import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  localISODate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Status = 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'TERMINATED'
type ContractType = 'NDA' | 'MSA' | 'SOW' | 'ICA' | 'DPA' | 'PO' | 'AMENDMENT'
type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }

type VendorContract = {
  id: number
  contract_number: string
  title: string
  contract_type: ContractType
  parent_contract: number | null
  parent_contract_number: string | null
  version: string
  partner: number
  partner_name?: string
  partner_code?: string
  start_date: string
  end_date: string | null
  currency_code: string
  default_rate: string
  rate_unit: 'HOUR' | 'DAY'
  billing_cadence: 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'MILESTONE'
  status: Status
  renewal_notice_days: number
  is_currently_active?: boolean
  assignment_count?: number
}

// Minimal create payload
type CreateInput = {
  contract_number: string
  contract_type: ContractType
  partner: number | 0
  parent_contract: number | null
  start_date: string
}

// ─────────────────── Constants ───────────────────
const STATUS_BADGE: Record<Status, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-amber-100 text-amber-800',
  TERMINATED: 'bg-red-100 text-red-700',
}

const TYPE_BADGE: Record<ContractType, string> = {
  NDA: 'bg-slate-100 text-slate-700',
  MSA: 'bg-blue-100 text-blue-800',
  SOW: 'bg-indigo-100 text-indigo-800',
  ICA: 'bg-teal-100 text-teal-800',
  DPA: 'bg-purple-100 text-purple-800',
  PO: 'bg-amber-100 text-amber-800',
  AMENDMENT: 'bg-orange-100 text-orange-800',
}

const CONTRACT_TYPE_OPTIONS: { value: ContractType; label: string; short: string }[] = [
  { value: 'NDA', label: 'NDA', short: 'Non-Disclosure Agreement' },
  { value: 'MSA', label: 'MSA', short: 'Master Service Agreement' },
  { value: 'SOW', label: 'SOW', short: 'Statement of Work' },
  { value: 'ICA', label: 'ICA', short: 'Independent Contractor Agreement' },
  { value: 'DPA', label: 'DPA', short: 'Data Processing Agreement (GDPR)' },
  { value: 'PO', label: 'PO', short: 'Blanket Order (Framework)' },
  { value: 'AMENDMENT', label: 'Amendment', short: 'Amendment / Addendum' },
]

const STATUS_FILTERS: ('ALL' | Status)[] = ['ALL', 'DRAFT', 'ACTIVE', 'EXPIRED', 'TERMINATED']
const TYPE_FILTERS: ('ALL' | ContractType)[] = ['ALL', 'NDA', 'MSA', 'SOW', 'ICA', 'DPA', 'PO', 'AMENDMENT']

function calcDaysLeft(endDate: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const end = new Date(endDate); end.setHours(0, 0, 0, 0)
  return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

function expiryUrgencyClass(daysLeft: number): string {
  if (daysLeft < 0) return 'bg-red-100 text-red-800'
  if (daysLeft <= 7) return 'bg-red-100 text-red-800'
  if (daysLeft <= 30) return 'bg-amber-100 text-amber-800'
  return 'bg-blue-100 text-blue-800'
}

function expiryLabel(daysLeft: number): string {
  if (daysLeft < 0) return `${Math.abs(daysLeft)}d overdue`
  if (daysLeft === 0) return 'expires today'
  return `${daysLeft}d left`
}

export default function FinanceVendorContracts() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ['finance', 'vendor-contracts'],
    queryFn: async () => (await api.get<VendorContract[]>('/partners/vendor-contracts/')).data,
  })
  const { data: partners = [] } = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Status>('ALL')
  const [filterType, setFilterType] = useState<'ALL' | ContractType>('ALL')

  function openCreate() {
    const year = new Date().getFullYear()
    const rand = Math.random().toString(36).slice(2, 7).toUpperCase()
    setDraft({
      ...emptyDraft(),
      contract_number: `MSA-${year}-${rand}`,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<VendorContract>('/partners/vendor-contracts/', {
      contract_number: draft.contract_number,
      contract_type: draft.contract_type,
      partner: Number(draft.partner),
      parent_contract: draft.parent_contract || null,
      start_date: draft.start_date,
      title: '',
      end_date: null,
      currency_code: 'EUR',
      default_rate: '0.00',
      rate_unit: 'HOUR',
      status: 'DRAFT',
      signed_at: null,
      max_hours: null,
      billing_cadence: 'MONTHLY',
      payment_terms_days: 30,
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['finance', 'vendor-contracts'] })
      toast.success('Contract created')
      setCreating(false)
      navigate(`/admin/partners/vendor-contracts/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/partners/vendor-contracts/${id}/`),
    onSuccess: () => {
      toast.success('Contract deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'vendor-contracts'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const vendorPartners = partners.filter(
    (p) => p.is_active && (p.partner_type === 'VENDOR' || p.partner_type === 'BOTH' || p.partner_type === 'FREELANCER')
  )

  // Parent contract options: contracts from the selected vendor (for SOW / Amendment)
  const parentContractOptions = draft.partner
    ? contracts.filter((c) => c.partner === Number(draft.partner))
    : contracts

  const filtered = contracts
    .filter((c) => filterStatus === 'ALL' || c.status === filterStatus)
    .filter((c) => filterType === 'ALL' || c.contract_type === filterType)

  // Contracts needing attention: ACTIVE, has end_date, within their renewal notice window
  type ExpiringContract = VendorContract & { daysLeft: number }
  const expiringContracts: ExpiringContract[] = contracts
    .filter((c) => c.status === 'ACTIVE' && c.end_date != null)
    .map((c) => ({ ...c, daysLeft: calcDaysLeft(c.end_date!) }))
    .filter((c) => c.daysLeft <= (c.renewal_notice_days ?? 30))
    .sort((a, b) => a.daysLeft - b.daysLeft)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Vendor contracts</h2>
          <p className="text-sm text-muted-foreground">
            {contracts.length} total &middot; {contracts.filter((c) => c.status === 'ACTIVE').length} active
          </p>
        </div>
        <Button onClick={openCreate} disabled={vendorPartners.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New contract
        </Button>
      </div>

      {vendorPartners.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          Add at least one active <strong>vendor</strong> (or freelancer) partner before creating a contract.
        </div>
      )}

      {/* Expiry alert panel */}
      {expiringContracts.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 overflow-hidden">
          <div className="px-4 py-2 bg-amber-100 border-b border-amber-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-amber-900">
              {expiringContracts.length === 1
                ? '1 contract requires attention'
                : `${expiringContracts.length} contracts require attention`}
            </span>
            <span className="text-xs text-amber-700">within renewal notice window</span>
          </div>
          <div className="divide-y divide-amber-200">
            {expiringContracts.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between px-4 py-2 text-sm hover:bg-amber-100/60 cursor-pointer"
                onClick={() => navigate(`/admin/partners/vendor-contracts/${c.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold shrink-0 ${TYPE_BADGE[c.contract_type] ?? 'bg-muted text-muted-foreground'}`}>
                    {c.contract_type}
                  </span>
                  <span className="font-mono text-xs text-amber-900 shrink-0">{c.contract_number}</span>
                  <span className="text-muted-foreground truncate">{c.partner_name}</span>
                  {c.title && <span className="text-muted-foreground truncate hidden md:block">&mdash; {c.title}</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <span className="text-xs text-muted-foreground font-mono">{c.end_date}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${expiryUrgencyClass(c.daysLeft)}`}>
                    {expiryLabel(c.daysLeft)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status + Type filter row */}
      <div className="mb-4 flex items-center gap-2 text-sm flex-wrap">
        <span className="text-muted-foreground w-14 shrink-0">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
        <span className="w-px h-5 bg-border mx-1 shrink-0" />
        <Select value={filterType} onValueChange={(v) => setFilterType(v as 'ALL' | ContractType)}>
          <SelectTrigger className="h-7 w-36 text-xs">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All types</SelectItem>
            {TYPE_FILTERS.filter((t) => t !== 'ALL').map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading&hellip;</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No vendor contracts.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Type</Th>
                <Th>Vendor</Th>
                <Th>Period</Th>
                <Th>Status</Th>
                <Th className="text-right">Assign.</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/partners/vendor-contracts/${c.id}`)}
                >
                  <Td className="font-mono text-xs">
                    {c.contract_number}
                    {c.parent_contract_number && (
                      <div className="text-muted-foreground mt-0.5">
                        under {c.parent_contract_number}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_BADGE[c.contract_type] ?? 'bg-muted text-muted-foreground'}`}>
                      {c.contract_type}
                    </span>
                  </Td>
                  <Td>{c.partner_name}</Td>
                  <Td className="text-xs">
                    {c.start_date} &rarr; {c.end_date || <span className="text-muted-foreground">open</span>}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_BADGE[c.status]}`}>{c.status}</span>
                      {c.is_currently_active && <span className="text-xs text-green-700">&#9679;</span>}
                    </div>
                  </Td>
                  <Td className="text-right text-xs">{c.assignment_count ?? 0}</Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/partners/vendor-contracts/${c.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={(c.assignment_count ?? 0) > 0}
                          onClick={() => (c.assignment_count ?? 0) === 0 && confirm(
                            `contract ${c.contract_number}`,
                            () => deleteMut.mutate(c.id),
                          )}
                          className={(c.assignment_count ?? 0) === 0 ? 'text-destructive' : ''}
                        >
                          {(c.assignment_count ?? 0) === 0
                            ? 'Delete'
                            : `Delete (${c.assignment_count} assignments)`}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New vendor contract"
        description="Select the contract type, then fill in the contract number and vendor. Title, dates, rates, and billing terms are completed on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="vc-type">Contract type *</FieldLabel>
            <Select value={draft.contract_type}
              onValueChange={(v) => {
                const year = new Date().getFullYear()
                const rand = Math.random().toString(36).slice(2, 7).toUpperCase()
                setDraft({ ...draft, contract_type: v as ContractType, parent_contract: null, contract_number: `${v}-${year}-${rand}` })
              }}>
              <SelectTrigger id="vc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRACT_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label} &mdash; {o.short}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="vc-num">Contract number *</FieldLabel>
            <Input id="vc-num" autoFocus required value={draft.contract_number}
              onChange={(ev) => setDraft({ ...draft, contract_number: ev.target.value })}
              className="font-mono" />
          </Field>

          <Field>
            <FieldLabel htmlFor="vc-vendor">Vendor *</FieldLabel>
            <SearchableSelect
              id="vc-vendor"
              value={draft.partner ? String(draft.partner) : undefined}
              onChange={(v) => setDraft({ ...draft, partner: Number(v), parent_contract: null })}
              placeholder="select vendor"
              options={vendorPartners.map((p) => ({
                value: String(p.id), label: p.name, hint: p.code,
              }))}
            />
            <FieldDescription>Active vendor, freelancer, or both-type partners.</FieldDescription>
          </Field>

          {(draft.contract_type === 'SOW' || draft.contract_type === 'AMENDMENT' || draft.contract_type === 'DPA') && (
            <Field>
              <FieldLabel htmlFor="vc-parent">
                Parent contract{draft.contract_type === 'AMENDMENT' || draft.contract_type === 'DPA' ? ' *' : ''}
              </FieldLabel>
              <SearchableSelect
                id="vc-parent"
                value={draft.parent_contract ? String(draft.parent_contract) : undefined}
                onChange={(v) => setDraft({ ...draft, parent_contract: v ? Number(v) : null })}
                placeholder="select parent contract"
                options={parentContractOptions.map((c) => ({
                  value: String(c.id),
                  label: c.contract_number,
                  hint: c.title || c.partner_name || '',
                }))}
              />
              <FieldDescription>
                {draft.contract_type === 'SOW' && 'The MSA or ICA this SOW is issued under.'}
                {draft.contract_type === 'AMENDMENT' && 'Required. The contract this amendment modifies.'}
                {draft.contract_type === 'DPA' && 'Required. The service agreement this DPA is attached to.'}
              </FieldDescription>
            </Field>
          )}

          {draft.contract_type === 'PO' && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <strong>Blanket Order:</strong> This creates a framework contract that authorises spending up to an agreed value.
              Individual purchase orders (with line items and receipt tracking) are managed in the{' '}
              <strong>Procurement</strong> module and can reference this blanket order.
            </div>
          )}

        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    contract_number: '',
    contract_type: 'MSA',
    partner: 0,
    parent_contract: null,
    start_date: localISODate(new Date()),
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
